import {useCallback, useEffect, useRef, useState} from 'react';
import axios from 'axios';
import qs from 'qs';
import _ from "lodash";
import {useHistory, useLocation} from "react-router-dom";
import useEffectExceptOnMount from "../helpers/useEffectExceptOnMount";

export function usePrevious(value) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
}

export const useDebouncedCallback = (callback, delay = 1000) => {
  const timeoutRef = useRef(null);

  return useCallback((...args) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      callback(...args);
    }, delay);
  }, [callback, delay]);
};

export function useQuery(location) {
  return qs.parse(location.search, { ignoreQueryPrefix: true });
}

export function usePrevValues(value, callback) {
  const prevValues = useRef(value);

  useEffect(() => {
    callback(prevValues.current);
    return () => (prevValues.current = value);
  }, [value, callback]);
};

export function useField(type, required, initialValue, afterChange=()=>{}) {
  const [value, setValue] = useState(initialValue);
  const [error, setError] = useState(required && _.isEmpty(initialValue));

  useEffect(() => {
    setError(required && _.isEmpty(initialValue))
  }, [initialValue])

  const onChange = (event) => {
    const val = event.target.value
    setError(required && _.isEmpty(val));
    setValue(val);
    afterChange(event);
  }
  const reset = () =>  {
    setValue('')
    setError(false)
  }

  useEffect(() => {
    setValue( initialValue );
  }, [ initialValue ])

  return {
    type,
    required,
    value,
    onChange,
    error,
    reset
  }
}

export function useFieldWithValidation(type, initialValue, valid, required=true, afterChange=()=>{}, errorMessage) {
  const calculateError = (value) => {
    if (typeof valid === "function") {
      return !valid(value);
    }

    return false
  }

  const [value, setValue] = useState(initialValue);
  const [error, setError] = useState(calculateError(initialValue));
  useEffectExceptOnMount(() => {
    setValue(initialValue);
    setError(calculateError(initialValue));
  }, [initialValue]);
  const onChange = (event) => {
    setError(calculateError(event.target.value));
    setValue(event.target.value);
    afterChange(event);
  }
  const reset = () =>  {
    setValue('')
    setError(required && calculateError(''))
  }
  const validate = () => {
    setError(required && calculateError(value));
  }

  return {
    type,
    required,
    value,
    onChange,
    error,
    reset,
    validate,
    errorMessage: error && errorMessage ? errorMessage(value) : null,
  }
}

export function useOption(required, initialValue) {
  const hasErrors = (option) => (required && (_.isEmpty(option)));

  const [value, setValue] = useState(initialValue);
  const [error, setError] = useState(hasErrors(initialValue));

  const setOption = (option) => {
      setError(hasErrors(option));
      setValue(option);
  }

  const reset = () =>  {
    setValue(initialValue)
    setError(true);
  }

  return {
    value,
    setOption,
    error,
    reset,
    required
  }
}

export function useRefreshableLocation(handleQueryParams, delayed=false) {
  const location = useLocation();
  let history = useHistory();
  const queryParams = useQuery(location);

  const refresh = (search='', pathname=location.pathname, state=location.state) => {
    history.replace({ pathname, search, state})
    const query = qs.parse(search, { ignoreQueryPrefix: true })
    handleQueryParams(query, state)
  }

  useEffect(() => {
    if (delayed) {
      setTimeout(() => {handleQueryParams(queryParams, location.state)}, 500)
    } else {
      handleQueryParams(queryParams, location.state)
    }
  }, [location]);

  return {
    location,
    history,
    refresh
  }
}

export function useLoginErrorHandler({ onUnathorizedError }) {
  const errorInterceptor = axios.interceptors.response.use(
      res => res,
      error => {
        if (error?.response?.status) {
          switch (error.response.status) {
            case 401:
              onUnathorizedError();
              throw error;
            case 429:
              onUnathorizedError();
              throw error;
            default:
              throw error;
          }
        } else { // Occurs for axios error.message = 'Network Error'
          throw error;
        }
      }
  );

  useEffect(() => {
    return () => {
      axios.interceptors.response.eject(errorInterceptor);
    };
  });
}

export function useBannedUserErrorHandler({ onBannedUserError }) {
  const errorInterceptor = axios.interceptors.response.use(
      res => res,
      error => {
        if (error?.response?.status) {
          switch (error.response.status) {
            case 429:
              onBannedUserError();
              throw error;
            default:
              throw error;
          }
        } else { // Occurs for axios error.message = 'Network Error'
          throw error;
        }
      }
  );

  useEffect(() => {
    return () => {
      axios.interceptors.response.eject(errorInterceptor);
    };
  });
}

export function useAdminServerVersion() {
  const [versionData, setVersionData] = useState({version: 0, versionHasChanged: false})

  const errorInterceptor = axios.interceptors.response.use(
      res => {
        const serverVersion = parseInt(res.headers['x-admin-server-version']) || 0
        if (serverVersion !== 0 && versionData.version !== serverVersion) {
          setVersionData({
            version: serverVersion,
            versionHasChanged: versionData.version !== 0,
          })
        }
        return res
      },
      error => { throw error }
  );

  useEffect(() => {
    return () => {
      axios.interceptors.response.eject(errorInterceptor);
    };
  });

  return [versionData.versionHasChanged]
}

export function useInterval(callback, delay) {
  const savedCallback = useRef();
  const poll = useRef();

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  const start = (delay) => {
    if (delay !== null) {
      poll.current = setInterval(() => savedCallback.current(), delay);
    }
  }
  const stop = () => {
    if (poll.current) {
      clearInterval(poll.current);
      poll.current = null;
    }
  }

  return [start, stop];
}
